import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { getService } from '@Client/runner.hooks/useService';
import {
  GridColumn as Column,
  GridCustomCellProps,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
  State
} from '@progress/kendo-data-query';
import KendoReactServerGrid, {
  GridCustomFilterCellProps,
  GridDataStateChangeEvent
} from '@Shared.Angular/kendo.react/KendoReactServerGrid';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';
import GridTextFilter from '@Shared.Angular/flowingly.components/Filters/GridTextFilter';
import { IUserSummary } from '@Shared.Angular/@types/core/contracts/queryModel/user/userSummary';
import ActionButtons from '@Client/runner.library/ActionButtons';
import {
  IUserCreatedMessage,
  IUserDeletedMessage,
  IUserUpdatedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import { useRemoteDataSource } from '@progress/kendo-react-data-tools';

const SetupUsers = () => {
  const [filter, setFilter] = useState<CompositeFilterDescriptor>(null);
  const dataState = useRef<State>({});
  const [total, setTotal] = useState(0);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const setupApiService = getService('setupApiService');
  const dataSource = useRemoteDataSource<ISetupUserSummary>({
    transport: {
      read: (options): Promise<ISetupUserSummary[]> => {
        const apiOptions = { ...options, pageSize: options.take };
        const usersPromise = new Promise<ISetupUserSummary[]>(
          (resolve, reject) => {
            setupApiService
              .getUsersWithOptions(apiOptions)
              .then((response) => {
                setTotal(response.total);
                setInitialDataLoaded(true);
                resolve(response.users.map(mapUserToSetupUser));
              })
              .catch(() => {
                reject('Error loading users');
              });
          }
        );
        return usersPromise;
      }
    },
    defaultTake: 25,
    serverPaging: true,
    serverSorting: true,
    serverFiltering: true,
    schema: {
      model: {
        id: 'id'
      }
    }
  });

  useEffect(() => {
    dataState.current = dataSource.dataState;
    dataSource.read();

    const subscriberId = 'setup-users';
    const pubSubService = getService('pubsubService');
    pubSubService.subscribeMany(
      ['SIGNALR_USER_CREATED', 'SIGNALR_USER_UPDATED', 'SIGNALR_USER_DELETED'],
      (
        event: PubSubEvent,
        message: IUserCreatedMessage | IUserUpdatedMessage | IUserDeletedMessage
      ) => {
        dataSource.setDataState(dataState.current);
        dataSource.read();
      },
      subscriberId
    );

    return () => {
      pubSubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const mapUserToSetupUser = (user: IUserSummary) => {
    return {
      ...user,
      roleNames: user.roles
        .map((r) => r.name)
        .sort()
        .join(', '),
      teamNames: user.teams
        .map((t) => t.displayName)
        .sort()
        .join(', ')
    } as ISetupUserSummary;
  };

  const openEditUserModal = useCallback(async (user: ISetupUserSummary) => {
    const dialogService = getService('dialogService');
    dialogService.showDialog({
      template:
        'Client/runner.setup/runner.setup.users/runner.setup.users.dialog.tmpl.html',
      controller: 'userDialogController',
      appendClassName: 'ngdialog-normal',
      data: user
    });
  }, []);

  const openDeleteUserModal = useCallback(async (user: ISetupUserSummary) => {
    const setupDeleteApiService = getService('setupDeleteApiService');
    const sessionService = getService('sessionService');
    const currentUserEmail = sessionService.getUser().email;
    const userToDelete = {
      email: user.email,
      id: user.id,
      fullName: user.fullName,
      isManager: user.isManager
    };
    setupDeleteApiService.tryDeleteUser(userToDelete, currentUserEmail);
  }, []);

  const NameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Name'),
    []
  );

  const EmailFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Email'),
    []
  );

  const [roleNames, setRoleNames] = useState<string[]>([]);
  useEffect(() => {
    const hideSpinner = true;
    getService('roleApiService')
      .getRoles(hideSpinner)
      .then((rolesFromApi) => {
        setRoleNames(rolesFromApi.map((r) => r.name));
      });
  }, []);
  const RolesFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, roleNames, 'Search by Role'),
    [roleNames]
  );

  const ManagerFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Manager'),
    []
  );

  const [teamNames, setTeamNames] = useState<string[]>([]);
  useEffect(() => {
    getService('setupApiService')
      .getTeamsWithOptions({})
      .then((teamsFromApi) => {
        setTeamNames(teamsFromApi.teams.map((t) => t.name));
      });
  }, []);
  const TeamsFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, teamNames, 'Search by Team'),
    [teamNames]
  );

  const StatusFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ['Active', 'Invited'], 'Search by Status'),
    []
  );

  const generateActionsCell = (props: GridCustomCellProps) => {
    const user: ISetupUserSummary = props.dataItem;
    let editHandler: () => void;
    let deleteHandler: () => void;
    const canEditUsers = getService(
      'permissionsService'
    ).currentUserHasPermission(FlowinglyPermissions.SETUP_USER_UPDATE);

    if (canEditUsers) {
      editHandler = () => openEditUserModal(user);
      deleteHandler = () => openDeleteUserModal(user);
    }
    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{
            edit: true,
            delete: true
          }}
          onEditClick={editHandler}
          onDeleteClick={deleteHandler}
        />
      </td>
    );
  };

  const initialSort = useMemo(
    () => [{ field: 'fullName', dir: 'asc' } as SortDescriptor],
    []
  );

  const apiRequestTimeout = React.useRef<NodeJS.Timeout>(null);
  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    if (event.dataState.filter) {
      event.dataState.filter.filters = event.dataState.filter.filters.filter(
        (f) => {
          return (
            (f as FilterDescriptor).value !== '' &&
            (f as FilterDescriptor).value != null
          );
        }
      );
    }
    const typingDebounceMilliseconds = 400;
    const delayMilliseconds =
      event.nativeEvent?.inputType === 'insertText'
        ? typingDebounceMilliseconds
        : 0;
    dataState.current = event.dataState;
    dataSource.setDataState(event.dataState);
    if (apiRequestTimeout.current) {
      clearTimeout(apiRequestTimeout.current);
    }
    apiRequestTimeout.current = setTimeout(() => {
      dataSource.setDataState(event.dataState);
      dataSource.read();
    }, delayMilliseconds);
  };

  return (
    <div className="setup-users-container">
      <KendoReactServerGrid
        id="setup-users-grid"
        data={dataSource.data}
        dataState={dataSource.dataState}
        onDataStateChange={handleDataStateChange}
        dataItemKey="id"
        defaultSort={initialSort}
        filter={filter}
        filterable={true}
        onFilterChange={(a) => {
          setFilter(a.filter);
        }}
        pageable={{
          buttonCount: 10,
          info: true,
          previousNext: true
        }}
        sortable={true}
        total={total}
      >
        <Column
          field="fullName"
          title="Name"
          className="column-name"
          cells={{ filterCell: NameFilterCellMemo }}
        />
        <Column
          field="email"
          title="Email"
          className="column-email"
          cells={{ filterCell: EmailFilterCellMemo }}
        />
        <Column
          field="roleNames"
          title="Roles"
          className="column-role-names"
          sortable={false}
          cells={{ filterCell: RolesFilterCellMemo }}
        />
        <Column
          field="managerName"
          title="Manager"
          className="column-manager-name"
          cells={{ filterCell: ManagerFilterCellMemo }}
        />
        <Column
          field="teamNames"
          title="Teams"
          className="column-team-names"
          sortable={false}
          cells={{ filterCell: TeamsFilterCellMemo }}
        />
        <Column
          field="status"
          title="Status"
          className="column-status"
          cells={{ filterCell: StatusFilterCellMemo }}
        />
        <Column
          title="Actions"
          className="column-actions"
          width="100"
          sortable={false}
          filterable={false}
          resizable={false}
          cells={{ data: generateActionsCell }}
        />
        <GridNoRecords>
          <span data-testid="setup-users-no-records">
            {initialDataLoaded ? 'No Users found' : 'Loading...'}
          </span>
        </GridNoRecords>
      </KendoReactServerGrid>
    </div>
  );
};

export default SetupUsers;

interface ISetupUserSummary extends IUserSummary {
  roleNames: string;
  teamNames: string;
}
